
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateHotelPath } from '../utils/slugUtils';
import { useLocalization } from '../hooks/useLocalization';
import type { TravelMateResponse, TravelMateSuggestionChip, HolidayPackage, SearchQuery } from '../types';
import { SearchType } from '../types';
import { sendChatToAI } from '../services/aiService';
import { TravelVisualSearch } from './TravelVisualSearch';
import {
    performChatSearch, ChatSearchParams, generateBookingUrl, initializeLocationCache,
    getChatDepartures, getChatArrivals, getChatDates, getChatNights, setChatNights,
    executeChatSearch, getChatState, getCollectedParamsSummary, resetChatSearch,
    getChatSearchTypeChips, setChatSearchType,
    type ChatChip
} from '../services/chatTourvisioService';
import { socketService } from '../services/socketService';
import jsPDF from 'jspdf';

// Web Speech API types
declare global {
    interface Window {
        SpeechRecognition: any;
        webkitSpeechRecognition: any;
    }
}

interface Message {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content?: string;
    data?: TravelMateResponse;
    detectedLanguage?: string;
    searchResults?: HolidayPackage[];
}

// Simple language detection based on common words and characters
const detectLanguage = (text: string): string => {
    const lowerText = text.toLowerCase();

    // Turkish indicators
    const turkishChars = /[ğüşıöçĞÜŞİÖÇ]/;
    const turkishWords = ['merhaba', 'nasıl', 'otel', 'için', 'istiyorum', 'bana', 'göster', 'lütfen', 'tatil', 'plaj', 'deniz', 'nedir', 'var', 'mi', 'mı'];

    // Swedish indicators
    const swedishChars = /[åäöÅÄÖ]/;
    const swedishWords = ['hej', 'jag', 'vill', 'hotell', 'strand', 'resa', 'för', 'kan', 'hur', 'vad', 'bra', 'tack', 'och'];

    // German indicators
    const germanWords = ['ich', 'möchte', 'hotel', 'strand', 'urlaub', 'wie', 'was', 'für', 'und', 'bitte', 'danke', 'suche'];

    // French indicators
    const frenchWords = ['je', 'veux', 'hôtel', 'plage', 'voyage', 'pour', 'comment', 'quoi', 'merci', 'bonjour', 'cherche'];

    // Spanish indicators
    const spanishWords = ['hola', 'quiero', 'hotel', 'playa', 'viaje', 'para', 'cómo', 'qué', 'gracias', 'busco'];

    // Check for Turkish
    if (turkishChars.test(text) || turkishWords.some(word => lowerText.includes(word))) {
        return 'tr';
    }

    // Check for Swedish
    if (swedishChars.test(text) || swedishWords.some(word => lowerText.includes(word))) {
        return 'sv';
    }

    // Check for German
    if (germanWords.some(word => lowerText.includes(word))) {
        return 'de';
    }

    // Check for French
    if (frenchWords.some(word => lowerText.includes(word))) {
        return 'fr';
    }

    // Check for Spanish
    if (spanishWords.some(word => lowerText.includes(word))) {
        return 'es';
    }

    // Default to English
    return 'en';
};

// Global Chat Widget - Appears on all pages
// Matches site design (Blue-950/Blue-600/Amber-500 theme)
export const GlobalChatWidget: React.FC = () => {
    const { t, language: siteLanguage } = useLocalization();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [conversationLanguage, setConversationLanguage] = useState<string>(siteLanguage);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Voice features state
    const [isListening, setIsListening] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [autoSpeak, setAutoSpeak] = useState(false);
    const recognitionRef = useRef<any>(null);
    const synthesisRef = useRef<SpeechSynthesisUtterance | null>(null);

    // Get speech language code
    const getSpeechLang = (lang: string): string => {
        const langMap: Record<string, string> = {
            'sv': 'sv-SE',
            'en': 'en-US',
            'da': 'da-DK',
            'tr': 'tr-TR',
            'de': 'de-DE',
            'fr': 'fr-FR',
            'es': 'es-ES'
        };
        return langMap[lang] || 'en-US';
    };

    // Initialize Tourvisio location cache for chat search
    useEffect(() => {
        initializeLocationCache().catch(err =>
            console.warn('[Chat] Failed to initialize location cache:', err)
        );
    }, []);

    // Initialize speech recognition
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = false;
            recognitionRef.current.interimResults = false;

            recognitionRef.current.onresult = (event: any) => {
                const transcript = event.results[0][0].transcript;
                setInputValue(transcript);
                setIsListening(false);
            };

            recognitionRef.current.onerror = () => {
                setIsListening(false);
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
            };
        }
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, []);

    // Toggle voice listening
    const toggleListening = useCallback(() => {
        if (!recognitionRef.current) {
            alert(siteLanguage === 'sv' ? 'Röstinmatning stöds inte i denna webbläsare' : siteLanguage === 'da' ? 'Stemmeinput understøttes ikke i denne browser' : 'Voice input not supported in this browser');
            return;
        }

        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            recognitionRef.current.lang = getSpeechLang(siteLanguage);
            recognitionRef.current.start();
            setIsListening(true);
        }
    }, [isListening, siteLanguage]);

    // Text-to-speech function
    const speakText = useCallback((text: string, lang: string) => {
        if (!window.speechSynthesis) return;

        // Cancel any ongoing speech
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = getSpeechLang(lang);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        synthesisRef.current = utterance;
        window.speechSynthesis.speak(utterance);
    }, []);

    // Stop speaking
    const stopSpeaking = useCallback(() => {
        if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        }
    }, []);

    // Export conversation to PDF
    const exportToPDF = useCallback(() => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 20;
        let yPos = 20;

        // Header
        doc.setFontSize(20);
        doc.setTextColor(30, 58, 138); // Blue-950
        doc.text('Petra AI', margin, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text('AI Travel Assistant - Conversation Export', margin, yPos);
        yPos += 5;
        doc.text(new Date().toLocaleString(), margin, yPos);
        yPos += 15;

        // Messages
        doc.setFontSize(11);
        messages.forEach((msg) => {
            const isUser = msg.role === 'user';
            const text = msg.content || msg.data?.assistant_reply || '';

            if (yPos > 270) {
                doc.addPage();
                yPos = 20;
            }

            // Role label
            doc.setTextColor(isUser ? 37 : 30, isUser ? 99 : 58, isUser ? 235 : 138);
            doc.setFont('helvetica', 'bold');
            doc.text(isUser ? 'You:' : 'Petra:', margin, yPos);
            yPos += 6;

            // Message content
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(50);
            const lines = doc.splitTextToSize(text, pageWidth - (margin * 2));
            lines.forEach((line: string) => {
                if (yPos > 270) {
                    doc.addPage();
                    yPos = 20;
                }
                doc.text(line, margin, yPos);
                yPos += 6;
            });
            yPos += 8;
        });

        // Footer
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(150);
            doc.text(`lumitours.se - Page ${i} of ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
        }

        doc.save(`lumi-chat-${Date.now()}.pdf`);
    }, [messages]);

    // Share conversation
    const shareConversation = useCallback(async () => {
        const conversationText = messages.map(msg => {
            const role = msg.role === 'user' ? 'You' : 'Petra';
            const text = msg.content || msg.data?.assistant_reply || '';
            return `${role}: ${text}`;
        }).join('\n\n');

        const shareData = {
            title: 'Petra AI Conversation',
            text: conversationText,
            url: 'https://lumitours.se'
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                // User cancelled or error
            }
        } else {
            // Fallback: copy to clipboard
            try {
                await navigator.clipboard.writeText(conversationText);
                alert(siteLanguage === 'sv' ? 'Konversation kopierad!' : siteLanguage === 'da' ? 'Samtale kopieret!' : 'Conversation copied!');
            } catch {
                alert(siteLanguage === 'sv' ? 'Kunde inte dela' : siteLanguage === 'da' ? 'Kunne ikke dele' : 'Could not share');
            }
        }
    }, [messages, siteLanguage]);

    // Chat mode state: 'ai' = AI handles, 'waiting' = waiting for agent, 'human' = agent active
    const [chatMode, setChatMode] = useState<'ai' | 'waiting' | 'human'>('ai');
    const sessionId = useRef(`session_${Date.now()}`);

    // Request human agent
    const requestHumanAgent = useCallback(async () => {
        if (chatMode !== 'ai') return;

        setChatMode('waiting');

        // Connect to socket and request human
        socketService.connect();
        await socketService.syncSession({
            id: sessionId.current,
            userId: sessionId.current,
            messages: messages.map(m => ({
                sender: m.role === 'user' ? 'user' : 'assistant',
                text: m.content || m.data?.assistant_reply || '',
                timestamp: Date.now()
            })),
            status: 'waiting_for_agent',
            language: siteLanguage
        });

        try {
            await fetch('/api/chat/request-human', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sessionId: sessionId.current,
                    userId: sessionId.current,
                    reason: 'User clicked talk to agent button',
                    language: siteLanguage
                })
            });
        } catch (err) {
            console.error('[Chat] Failed to request human agent:', err);
        }

        // Add system message
        const waitingMsg: Message = {
            id: Date.now().toString(),
            role: 'system',
            content: siteLanguage === 'sv'
                ? '🙋 Din förfrågan om en riktig agent har skickats. Vänligen vänta...'
                : siteLanguage === 'da'
                    ? '🙋 Din anmodning om en rigtig agent er sendt. Vent venligst...'
                    : siteLanguage === 'tr'
                        ? '🙋 Canlı destek talebiniz iletildi. Lütfen bekleyin...'
                        : '🙋 Your request for a live agent has been sent. Please wait...'
        };
        setMessages(prev => [...prev, waitingMsg]);
    }, [chatMode, messages, siteLanguage]);

    // Listen for agent messages
    useEffect(() => {
        if (chatMode !== 'waiting' && chatMode !== 'human') return;

        const handleAgentMessage = (data: any) => {
            if (data.from === 'agent' || data.sender === 'admin') {
                setChatMode('human');
                const agentMsg: Message = {
                    id: Date.now().toString(),
                    role: 'assistant',
                    content: `👤 ${data.text || data.message}`
                };
                setMessages(prev => [...prev, agentMsg]);
            }
        };

        socketService.onMessage(handleAgentMessage);

        return () => {
            socketService.off('new_message');
        };
    }, [chatMode]);

    // Get localized welcome message
    const getWelcomeMessage = (lang: string): string => {
        const messages: Record<string, string> = {
            'sv': 'Hej! Jag är Petra, din AI-reseassistent. Fråga mig om destinationer, hotell, priser eller boka en resa!',
            'da': 'Hej! Jeg er Petra, din AI-rejseassistent. Spørg mig om destinationer, hoteller, priser eller book en rejse!',
            'en': 'Hello! I\'m Petra, your AI travel assistant. Ask me about destinations, hotels, prices or book a trip!'
        };
        return messages[lang] || messages['en'];
    };

    // Initialize with welcome message and update when language changes
    useEffect(() => {
        if (isOpen) {
            const welcomeMsg = getWelcomeMessage(siteLanguage);

            // Always update welcome message when opening or when language changes
            setMessages(prev => {
                // If there are only the welcome message or no messages, reset with new language
                if (prev.length === 0 || (prev.length === 1 && prev[0].id === 'welcome')) {
                    return [
                        {
                            id: 'welcome',
                            role: 'assistant',
                            content: welcomeMsg,
                            detectedLanguage: siteLanguage
                        }
                    ];
                }
                // If there are other messages (user has started chatting), keep them
                return prev;
            });
            setConversationLanguage(siteLanguage);
        }
    }, [isOpen, siteLanguage]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = async (customInput?: string) => {
        const text = customInput || inputValue;
        if (!text.trim() || isLoading) return;

        // Detect language from user input
        const detectedLang = detectLanguage(text);
        setConversationLanguage(detectedLang);

        const userMsg: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: text,
            detectedLanguage: detectedLang
        };

        setMessages(prev => [...prev, userMsg]);
        if (!customInput) setInputValue('');
        setIsLoading(true);

        try {
            // Build chat message history for context
            const chatHistory = messages.map(m => ({
                sender: m.role,
                text: m.content || m.data?.assistant_reply || ''
            }));

            // Add current user message
            chatHistory.push({ sender: 'user', text: text });

            // Build context string from current guided search state
            const currentState = getChatState();
            let chatContext = `Current guided search step: ${currentState.step}`;
            chatContext += getCollectedParamsSummary();

            // Use sendChatToAI with guided search context
            const responseJson = await sendChatToAI(
                chatHistory,
                detectedLang as 'en' | 'sv',
                chatContext
            );

            // Parse the AI response
            let parsedResponse: TravelMateResponse;
            try {
                parsedResponse = JSON.parse(responseJson);
            } catch {
                parsedResponse = {
                    assistant_reply: responseJson,
                    status: 'success',
                    budget_analysis: { original_cost: 0, user_budget: 0, gap: 0 },
                    interactive_cards: [],
                    suggestion_chips: []
                };
            }

            const botMsg: Message = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                data: parsedResponse,
                detectedLanguage: detectedLang
            };
            setMessages(prev => [...prev, botMsg]);

            // Auto-speak AI response if enabled
            if (autoSpeak && parsedResponse.assistant_reply) {
                speakText(parsedResponse.assistant_reply, detectedLang);
            }

            // ========== GUIDED SEARCH FLOW HANDLER ==========
            const aiResponse = parsedResponse as any;
            const intent = aiResponse.intent;

            // Helper to convert ChatChips to suggestion chips and add as message
            const addChipsMessage = (chips: ChatChip[], headerText: string) => {
                const chipsSuggestions: TravelMateSuggestionChip[] = chips
                    .filter(c => c.type !== 1) // Skip country headers for clickable chips
                    .slice(0, 12) // Limit to 12 chips max
                    .map(c => ({
                        label: c.label,
                        action: c.value,
                        icon: c.icon || '📍'
                    }));

                if (chipsSuggestions.length > 0) {
                    setMessages(prev => [...prev, {
                        id: (Date.now() + 10).toString(),
                        role: 'assistant',
                        data: {
                            assistant_reply: headerText,
                            status: 'success',
                            budget_analysis: { original_cost: 0, user_budget: 0, gap: 0 },
                            interactive_cards: [],
                            suggestion_chips: chipsSuggestions
                        } as TravelMateResponse,
                        detectedLanguage: detectedLang
                    }]);
                }
            };

            if (intent === 'start_search') {
                // User mentioned a destination → start guided flow with Step 0: search type
                console.log('[Chat] Starting guided search flow - asking search type');
                resetChatSearch();

                const typeResult = getChatSearchTypeChips();
                const headerText = detectedLang === 'sv'
                    ? '🔍 Vad letar du efter?'
                    : detectedLang === 'da'
                        ? '🔍 Hvad leder du efter?'
                        : detectedLang === 'tr'
                            ? '🔍 Ne arıyorsunuz?'
                            : '🔍 What are you looking for?';
                addChipsMessage(typeResult.chips, headerText);

            } else if (intent === 'select_search_type') {
                // User selected search type → route to next step
                const selectedType = (aiResponse.selected_value || text).toLowerCase();
                const isHotelOnly = selectedType.includes('hotel_only') || selectedType.includes('bara hotell') || selectedType.includes('hotel only') || selectedType === '🏨';
                const searchType = isHotelOnly ? 'hotel_only' : 'package';
                setChatSearchType(searchType as 'package' | 'hotel_only');
                console.log('[Chat] Search type selected:', searchType);

                if (searchType === 'hotel_only') {
                    // Hotel only → skip departure, ask destination directly
                    // Set Stockholm as default departure for API compatibility
                    const depResult = await getChatDepartures();
                    const stockholm = depResult.chips.find(c => c.label.toLowerCase().includes('stockholm'));
                    if (stockholm) {
                        const arrResult = await getChatArrivals(stockholm.value, stockholm.label, stockholm.type || 2);
                        const headerText = detectedLang === 'sv'
                            ? '🏖️ Vilken destination söker du hotell i?'
                            : detectedLang === 'da'
                                ? '🏖️ Hvilken destination søger du hotel i?'
                                : detectedLang === 'tr'
                                    ? '🏖️ Hangi destinasyonda otel arıyorsunuz?'
                                    : '🏖️ Which destination are you looking for a hotel in?';
                        addChipsMessage(arrResult.chips, headerText);
                    }
                } else {
                    // Package → ask departure first
                    const depResult = await getChatDepartures();
                    const headerText = detectedLang === 'sv'
                        ? '✈️ Välj din avresestad:'
                        : detectedLang === 'da'
                            ? '✈️ Vælg din afrejseby:'
                            : detectedLang === 'tr'
                                ? '✈️ Kalkış şehrinizi seçin:'
                                : '✈️ Select your departure city:';
                    addChipsMessage(depResult.chips, headerText);
                }

            } else if (intent === 'select_departure') {
                // User selected departure → Step 2: get destinations
                const selectedValue = aiResponse.selected_value || text;
                console.log('[Chat] Departure selected:', selectedValue);

                // Find the departure in our cached data
                const depResult = await getChatDepartures();
                const matchedDep = depResult.chips.find(c =>
                    c.label.toLowerCase().includes(selectedValue.toLowerCase()) ||
                    c.value === selectedValue
                );

                if (matchedDep) {
                    const arrResult = await getChatArrivals(matchedDep.value, matchedDep.label, matchedDep.type || 2);
                    const headerText = detectedLang === 'sv'
                        ? '🏖️ Vart vill du resa? Välj destination:'
                        : detectedLang === 'da'
                            ? '🏖️ Hvor vil du hen? Vælg destination:'
                            : detectedLang === 'tr'
                                ? '🏖️ Nereye gitmek istiyorsunuz? Destinasyon seçin:'
                                : '🏖️ Where do you want to go? Select a destination:';
                    addChipsMessage(arrResult.chips, headerText);
                }

            } else if (intent === 'select_destination') {
                // User selected destination → Step 3: get dates
                const selectedValue = aiResponse.selected_value || aiResponse.search_params?.destination || text;
                console.log('[Chat] Destination selected:', selectedValue);

                // If we don't have departure yet, use Stockholm as default
                const state = getChatState();
                if (!state.departureId) {
                    const depResult = await getChatDepartures();
                    const stockholm = depResult.chips.find(c => c.label.toLowerCase().includes('stockholm'));
                    if (stockholm) {
                        await getChatArrivals(stockholm.value, stockholm.label, stockholm.type || 2);
                    }
                }

                // Find the destination from cached arrivals
                const depResult2 = await getChatDepartures();
                const arrResult = await getChatArrivals(
                    getChatState().departureId || '10',
                    getChatState().departureName || 'Stockholm'
                );
                const matchedDest = arrResult.chips.find(c =>
                    c.label.toLowerCase().includes(selectedValue.toLowerCase()) ||
                    c.value === selectedValue
                );

                if (matchedDest) {
                    const dateResult = await getChatDates(matchedDest.value, matchedDest.label, matchedDest.type || 2);
                    if (dateResult.chips.length > 0) {
                        const headerText = detectedLang === 'sv'
                            ? '📅 Välj resedatum:'
                            : detectedLang === 'da'
                                ? '📅 Vælg rejsedato:'
                                : detectedLang === 'tr'
                                    ? '📅 Seyahat tarihinizi seçin:'
                                    : '📅 Select your travel date:';
                        addChipsMessage(dateResult.chips, headerText);
                    } else {
                        // No dates available, inform user
                        setMessages(prev => [...prev, {
                            id: (Date.now() + 10).toString(),
                            role: 'assistant',
                            content: detectedLang === 'sv'
                                ? '⚠️ Inga tillgängliga datum just nu. Prova en annan destination.'
                                : detectedLang === 'da'
                                    ? '⚠️ Ingen tilgængelige datoer lige nu. Prøv en anden destination.'
                                    : '⚠️ No available dates right now. Try another destination.'
                        }]);
                    }
                }

            } else if (intent === 'select_date') {
                // User selected date → Step 4: get nights
                const selectedValue = aiResponse.selected_value || aiResponse.search_params?.checkIn || text;
                console.log('[Chat] Date selected:', selectedValue);

                const nightResult = await getChatNights(selectedValue);
                if (nightResult.chips.length > 0) {
                    const headerText = detectedLang === 'sv'
                        ? '🌙 Hur många nätter?'
                        : detectedLang === 'da'
                            ? '🌙 Hvor mange nætter?'
                            : detectedLang === 'tr'
                                ? '🌙 Kaç gece kalmak istiyorsunuz?'
                                : '🌙 How many nights?';
                    addChipsMessage(nightResult.chips, headerText);
                }

            } else if (intent === 'select_nights') {
                // User selected nights → Step 5: ask travelers
                const nightsValue = parseInt(aiResponse.selected_value || aiResponse.search_params?.nights || text) || 7;
                console.log('[Chat] Nights selected:', nightsValue);

                const travelerResult = setChatNights(nightsValue);
                const headerText = detectedLang === 'sv'
                    ? '👥 Hur många resenärer?'
                    : detectedLang === 'da'
                        ? '👥 Hvor mange rejsende?'
                        : detectedLang === 'tr'
                            ? '👥 Kaç yolcu?'
                            : '👥 How many travelers?';
                addChipsMessage(travelerResult.chips, headerText);

            } else if (intent === 'select_travelers') {
                // User specified travelers → Step 6: execute search!
                const adultsParam = aiResponse.search_params?.adults || 2;
                const childrenParam = aiResponse.search_params?.children || 0;
                console.log('[Chat] Travelers set:', adultsParam, 'adults,', childrenParam, 'children');

                // Show searching indicator
                const searchingMsg: Message = {
                    id: (Date.now() + 20).toString(),
                    role: 'system',
                    content: detectedLang === 'sv' ? '🔍 Söker efter paket...' :
                        detectedLang === 'da' ? '🔍 Søger efter pakker...' :
                        detectedLang === 'tr' ? '🔍 Paketler aranıyor...' :
                            '🔍 Searching for packages...'
                };
                setMessages(prev => [...prev, searchingMsg]);

                try {
                    const searchResult = await executeChatSearch(adultsParam, childrenParam, [], detectedLang as 'en' | 'sv');

                    if (searchResult.success && searchResult.results.length > 0) {
                        setMessages(prev => {
                            const filtered = prev.filter(m => m.id !== searchingMsg.id);
                            const bookingUrl = generateBookingUrl(searchResult.searchParams);
                            return [...filtered, {
                                id: (Date.now() + 21).toString(),
                                role: 'assistant',
                                content: detectedLang === 'sv' ?
                                    `✅ Hittade ${searchResult.results.length} paket! Här är de bästa alternativen:` :
                                    detectedLang === 'da' ?
                                        `✅ Fandt ${searchResult.results.length} pakker! Her er de bedste muligheder:` :
                                    detectedLang === 'tr' ?
                                        `✅ ${searchResult.results.length} paket bulundu! İşte en iyi seçenekler:` :
                                        `✅ Found ${searchResult.results.length} packages! Here are the best options:`,
                                searchResults: searchResult.results as HolidayPackage[],
                                data: {
                                    assistant_reply: '',
                                    status: 'success',
                                    budget_analysis: { original_cost: 0, user_budget: 0, gap: 0 },
                                    interactive_cards: [],
                                    suggestion_chips: [],
                                    booking_url: bookingUrl
                                } as any
                            }];
                        });
                    } else {
                        setMessages(prev => {
                            const filtered = prev.filter(m => m.id !== searchingMsg.id);
                            return [...filtered, {
                                id: (Date.now() + 21).toString(),
                                role: 'assistant',
                                content: detectedLang === 'sv' ?
                                    '❌ Tyvärr hittade jag inga paket. Prova andra datum eller destination.' :
                                    detectedLang === 'da' ?
                                        '❌ Desværre fandt jeg ingen pakker. Prøv andre datoer eller destination.' :
                                    detectedLang === 'tr' ?
                                        '❌ Maalesef paket bulunamadı. Farklı tarih veya destinasyon deneyin.' :
                                        '❌ Sorry, no packages found. Try different dates or destination.'
                            }];
                        });
                    }
                } catch (searchError) {
                    console.error('[Chat] Search failed:', searchError);
                    setMessages(prev => prev.filter(m => m.id !== searchingMsg.id));
                }

            } else if (intent === 'quick_search') {
                // User provided everything at once → direct search
                console.log('[Chat] Quick search with all params:', aiResponse.search_params);

                const searchingMsg: Message = {
                    id: (Date.now() + 20).toString(),
                    role: 'system',
                    content: detectedLang === 'sv' ? '🔍 Söker efter paket...' :
                        detectedLang === 'da' ? '🔍 Søger efter pakker...' :
                        detectedLang === 'tr' ? '🔍 Paketler aranıyor...' :
                            '🔍 Searching for packages...'
                };
                setMessages(prev => [...prev, searchingMsg]);

                try {
                    const searchResult = await performChatSearch(
                        aiResponse.search_params as ChatSearchParams,
                        detectedLang as 'en' | 'sv'
                    );

                    if (searchResult.success && searchResult.results.length > 0) {
                        setMessages(prev => {
                            const filtered = prev.filter(m => m.id !== searchingMsg.id);
                            const bookingUrl = generateBookingUrl(aiResponse.search_params as ChatSearchParams);
                            return [...filtered, {
                                id: (Date.now() + 21).toString(),
                                role: 'assistant',
                                content: detectedLang === 'sv' ?
                                    `✅ Hittade ${searchResult.results.length} paket!` :
                                    detectedLang === 'da' ?
                                        `✅ Fandt ${searchResult.results.length} pakker!` :
                                    `✅ Found ${searchResult.results.length} packages!`,
                                searchResults: searchResult.results as HolidayPackage[],
                                data: {
                                    assistant_reply: '',
                                    status: 'success',
                                    budget_analysis: { original_cost: 0, user_budget: 0, gap: 0 },
                                    interactive_cards: [],
                                    suggestion_chips: [],
                                    booking_url: bookingUrl
                                } as any
                            }];
                        });
                    } else {
                        setMessages(prev => {
                            const filtered = prev.filter(m => m.id !== searchingMsg.id);
                            return [...filtered, {
                                id: (Date.now() + 21).toString(),
                                role: 'assistant',
                                content: detectedLang === 'sv' ?
                                    '❌ Inga paket hittades. Prova andra sökvillkor.' :
                                    detectedLang === 'da' ?
                                        '❌ Ingen pakker fundet. Prøv andre søgekriterier.' :
                                    '❌ No packages found. Try different search criteria.'
                            }];
                        });
                    }
                } catch (searchError) {
                    console.error('[Chat] Quick search failed:', searchError);
                    setMessages(prev => prev.filter(m => m.id !== searchingMsg.id));
                }

            } else if (intent === 'search' && aiResponse.search_params) {
                // Legacy: direct search intent (backwards compat)
                console.log('[Chat] Legacy search intent:', aiResponse.search_params);

                const searchingMsg: Message = {
                    id: (Date.now() + 2).toString(),
                    role: 'system',
                    content: detectedLang === 'sv' ? '🔍 Söker efter paket...' :
                        detectedLang === 'da' ? '🔍 Søger efter pakker...' :
                        detectedLang === 'tr' ? '🔍 Paketler aranıyor...' :
                            '🔍 Searching for packages...'
                };
                setMessages(prev => [...prev, searchingMsg]);

                try {
                    const searchResult = await performChatSearch(
                        aiResponse.search_params as ChatSearchParams,
                        detectedLang as 'en' | 'sv'
                    );

                    if (searchResult.success && searchResult.results.length > 0) {
                        setMessages(prev => {
                            const filtered = prev.filter(m => m.id !== searchingMsg.id);
                            const bookingUrl = generateBookingUrl(aiResponse.search_params as ChatSearchParams);
                            return [...filtered, {
                                id: (Date.now() + 3).toString(),
                                role: 'assistant',
                                content: `✅ Found ${searchResult.results.length} packages!`,
                                searchResults: searchResult.results as HolidayPackage[],
                                data: {
                                    assistant_reply: '',
                                    status: 'success',
                                    budget_analysis: { original_cost: 0, user_budget: 0, gap: 0 },
                                    interactive_cards: [],
                                    suggestion_chips: [],
                                    booking_url: bookingUrl
                                } as any
                            }];
                        });
                    } else {
                        setMessages(prev => {
                            const filtered = prev.filter(m => m.id !== searchingMsg.id);
                            return [...filtered, {
                                id: (Date.now() + 3).toString(),
                                role: 'assistant',
                                content: '❌ No packages found. Try different criteria.'
                            }];
                        });
                    }
                } catch (searchError) {
                    console.error('[Chat] Search failed:', searchError);
                    setMessages(prev => prev.filter(m => m.id !== searchingMsg.id));
                }
            }

        } catch (error: any) {
            console.error('Chat AI Error:', error);

            const errorMessages: Record<string, string> = {
                'sv': "Jag har lite svårt att svara just nu. Försök igen om en stund!",
                'en': "I'm having a bit of trouble right now. Please try again in a moment!"
            };

            setMessages(prev => [...prev, {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: errorMessages[siteLanguage] || errorMessages['en']
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const navigate = useNavigate();

    const handleCardClick = (pkg: HolidayPackage) => {
        // Build a proper SearchQuery from the chat state for the hotel detail page
        const chatS = getChatState();
        const query: SearchQuery = {
            type: chatS.searchType === 'hotel_only' ? SearchType.OnlyHotel : SearchType.PackageTours,
            departureId: chatS.departureId || '10',
            departureName: chatS.departureName || 'Stockholm',
            departureType: chatS.departureType || 2,
            destinationId: chatS.destinationId,
            destinationName: chatS.destinationName,
            destinationType: chatS.destinationType || 2,
            checkIn: chatS.checkIn,
            nights: chatS.nights || 7,
            rooms: 1,
            nationality: 'SE',
            currency: 'EUR',
            adults: chatS.adults || 2,
            children: chatS.children || 0,
            childrenAges: chatS.childrenAges || [],
            searchId: (pkg as any).searchId || '',
        };

        // Navigate with full hotel data and search query
        const hotelData = {
            ...pkg,
            offers: pkg.offers || [],
            searchId: (pkg as any).searchId,
        };

        navigate(generateHotelPath(hotelData as any), {
            state: { hotel: hotelData, searchQuery: query }
        });
        setIsOpen(false);
    };

    // Get placeholder text based on site language (browser language)
    const getPlaceholder = (): string => {
        // Only English and Swedish supported per business requirements
        const placeholders: Record<string, string> = {
            'sv': "T.ex: Antalya, strandnära med stor pool...",
            'en': "E.g: Antalya, beachfront with big pool..."
        };
        return placeholders[siteLanguage] || placeholders['en'];
    };

    return (
        <>
            {/* Floating Button - Site Design (Blue-600 with Amber hover) */}
            {/* Mobile: bottom-4 right-4, Desktop: bottom-6 right-6 */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[60] bg-gradient-to-br from-blue-600 to-indigo-700 text-white w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-2xl flex items-center justify-center hover:from-amber-500 hover:to-amber-600 hover:scale-110 transition-all duration-300 group"
            >
                <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-amber-500 rounded-full animate-pulse border-2 border-white"></div>
                <span className="text-xl sm:text-2xl group-hover:rotate-12 transition-transform">
                    {isOpen ? '✕' : '💎'}
                </span>
            </button>

            {/* Chat Panel - Full screen on mobile, floating on desktop */}
            {isOpen && (
                <div className="fixed inset-0 sm:inset-auto sm:bottom-24 sm:right-6 sm:w-[380px] sm:h-[550px] w-full h-full bg-white shadow-2xl z-[60] flex flex-col animate-slideIn sm:rounded-3xl sm:border sm:border-gray-200 overflow-hidden">
                    {/* Header - Compact Blue-950 gradient */}
                    <div className="bg-gradient-to-r from-blue-950 via-blue-900 to-blue-950 p-2.5 sm:p-3 flex items-center justify-between text-white shadow-xl relative overflow-hidden sm:rounded-t-3xl safe-area-top">
                        <div className="absolute inset-0 bg-amber-500/5 mix-blend-overlay"></div>
                        <div className="flex items-center gap-2 sm:gap-3 relative z-10">
                            <div className="relative">
                                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-tr from-indigo-500 to-blue-400 flex items-center justify-center shadow-xl border-2 border-white/20">
                                    <span className="text-sm sm:text-base">💎</span>
                                </div>
                                <span className="absolute bottom-0 right-0 block h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full ring-2 ring-blue-950 bg-green-400 shadow-sm"></span>
                            </div>
                            <div>
                                <h2 className="font-extrabold text-sm sm:text-base leading-none tracking-tight text-white">Petra AI</h2>
                                <span className="text-[7px] sm:text-[8px] text-amber-400 uppercase tracking-[0.15em] font-black opacity-90">AI RESEASSISTENT</span>
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-1 sm:gap-2 relative z-20">
                            {/* Auto-speak toggle */}
                            <button
                                onClick={() => setAutoSpeak(!autoSpeak)}
                                className={`p-2 rounded-full transition-all touch-manipulation ${autoSpeak ? 'bg-amber-500 text-white' : 'hover:bg-white/10 text-white/70 hover:text-white'}`}
                                aria-label={autoSpeak ? 'Disable auto-speak' : 'Enable auto-speak'}
                                title={siteLanguage === 'sv' ? 'Läs svar högt' : 'Read responses aloud'}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                </svg>
                            </button>

                            {/* PDF Export */}
                            <button
                                onClick={exportToPDF}
                                className="p-2 hover:bg-white/10 rounded-full transition-all text-white/70 hover:text-white touch-manipulation"
                                aria-label="Export to PDF"
                                title={siteLanguage === 'sv' ? 'Exportera till PDF' : 'Export to PDF'}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </button>

                            {/* Share */}
                            <button
                                onClick={shareConversation}
                                className="p-2 hover:bg-white/10 rounded-full transition-all text-white/70 hover:text-white touch-manipulation"
                                aria-label="Share conversation"
                                title={siteLanguage === 'sv' ? 'Dela konversation' : 'Share conversation'}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                </svg>
                            </button>

                            {/* Talk to Agent Button */}
                            <button
                                onClick={requestHumanAgent}
                                disabled={chatMode !== 'ai'}
                                className={`p-2 rounded-full transition-all touch-manipulation ${chatMode === 'waiting' ? 'bg-orange-500 text-white animate-pulse' :
                                    chatMode === 'human' ? 'bg-green-500 text-white' :
                                        'hover:bg-white/10 text-white/70 hover:text-white'
                                    }`}
                                aria-label="Talk to agent"
                                title={chatMode === 'waiting' ? (siteLanguage === 'sv' ? 'Väntar på agent...' : 'Waiting for agent...') :
                                    chatMode === 'human' ? (siteLanguage === 'sv' ? 'Agent online' : 'Agent online') :
                                        (siteLanguage === 'sv' ? 'Prata med en riktig person' : 'Talk to a real person')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </button>

                            {/* Close button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    stopSpeaking();
                                    setIsOpen(false);
                                }}
                                className="p-2 sm:p-2 hover:bg-white/10 rounded-full transition-all group active:scale-95 cursor-pointer touch-manipulation ml-1"
                                aria-label="Close chat"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Chat Area */}
                    <div className="flex-grow overflow-y-auto p-2.5 sm:p-3 space-y-4 sm:space-y-5 bg-gray-50/50 scroll-smooth">
                        {messages.map((msg, mIdx) => (
                            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : msg.role === 'system' ? 'justify-center' : 'justify-start'} animate-fadeInUp`} style={{ animationDelay: `${mIdx * 0.1}s` }}>

                                {/* System Message (searching indicator) */}
                                {msg.role === 'system' && (
                                    <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 animate-pulse">
                                        {msg.content}
                                    </div>
                                )}

                                {/* User and Assistant Messages */}
                                {msg.role !== 'system' && (
                                    <div className={`max-w-[92%] sm:max-w-[88%] ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-2xl rounded-tr-none' : 'bg-white text-gray-800 border border-gray-100 rounded-2xl rounded-tl-none'} p-3 sm:p-4 shadow-lg relative`}>
                                        {/* Text Content */}
                                        {msg.content && <p className="leading-relaxed whitespace-pre-wrap text-xs sm:text-[13px]">{msg.content}</p>}

                                        {/* Search Results Cards */}
                                        {msg.searchResults && msg.searchResults.length > 0 && (
                                            <div className="mt-4 space-y-3">
                                                {msg.searchResults.slice(0, 5).map((pkg, idx) => (
                                                    <div
                                                        key={pkg.id || idx}
                                                        onClick={() => handleCardClick(pkg)}
                                                        className="bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all group"
                                                    >
                                                        {/* Hotel Image - Full Width */}
                                                        {(pkg.thumbnailUrl || pkg.imageUrl) && (
                                                            <div className="relative h-28 overflow-hidden">
                                                                <img
                                                                    src={pkg.thumbnailUrl || pkg.imageUrl}
                                                                    alt={pkg.name}
                                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                                    onError={(e) => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=200&fit=crop'; }}
                                                                />
                                                                {/* Stars Badge */}
                                                                {(pkg.rating || 0) > 0 && (
                                                                    <div className="absolute top-2 left-2 flex items-center gap-0.5 px-2 py-0.5 bg-white/90 backdrop-blur-sm rounded-full">
                                                                        {[...Array(Math.min(Math.floor(pkg.rating || 0), 5))].map((_, i) => (
                                                                            <span key={i} className="text-amber-400" style={{ fontSize: '10px' }}>★</span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {/* Board Badge */}
                                                                {pkg.board && pkg.board !== 'N/A' && (
                                                                    <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full">
                                                                        {pkg.board}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {/* Content */}
                                                        <div className="p-3">
                                                            <h4 className="font-bold text-sm text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                                                                {pkg.name}
                                                            </h4>
                                                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                                                <span>📍 {pkg.location?.city || ''}</span>
                                                                {(pkg as any).nights && <span>🌙 {(pkg as any).nights} {siteLanguage === 'sv' ? 'nätter' : 'nights'}</span>}
                                                            </div>
                                                            <div className="flex items-center justify-between mt-2">
                                                                <span className="text-lg font-black bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                                                    {(pkg.totalPrice || pkg.pricePerNight || 0).toLocaleString()} {pkg.currency || 'EUR'}
                                                                </span>
                                                                <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-1 rounded-full font-semibold">
                                                                    {siteLanguage === 'sv' ? 'Visa →' : 'View →'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {msg.searchResults.length > 5 && (
                                                    <p className="text-xs text-gray-400 text-center">
                                                        + {msg.searchResults.length - 5} {siteLanguage === 'sv' ? 'fler alternativ' : 'more options'}
                                                    </p>
                                                )}
                                                {/* View All & Book CTA */}
                                                {(msg.data as any)?.booking_url && (
                                                    <a
                                                        href={(msg.data as any).booking_url}
                                                        className="mt-3 block w-full text-center bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl text-sm shadow-lg hover:shadow-xl transition-all"
                                                    >
                                                        {siteLanguage === 'sv' ? '🔍 Visa alla & Boka' : siteLanguage === 'tr' ? '🔍 Tümünü Gör & Rezerve Et' : '🔍 View All & Book'}
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                        {msg.data && (
                                            <div className="space-y-3 sm:space-y-4 mt-2">
                                                <p className="leading-relaxed font-semibold text-gray-800 text-xs sm:text-[14px] border-l-4 border-amber-400 pl-2.5">
                                                    {msg.data.assistant_reply}
                                                </p>

                                                {/* Route Insight Component */}
                                                {msg.data.route_insight && (
                                                    <div className="bg-blue-600 text-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl relative overflow-hidden group">
                                                        <div className="absolute top-0 right-0 p-3 sm:p-4 opacity-10 text-3xl sm:text-4xl transform group-hover:rotate-12 transition-transform select-none">📍</div>
                                                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 text-blue-100">ROUTE INSIGHT</h4>

                                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                                            <div>
                                                                <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest">VIBE</p>
                                                                <p className="text-sm font-black">{msg.data.route_insight.vibe}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest">TREND</p>
                                                                <p className="text-sm font-black">{msg.data.route_insight.trend}</p>
                                                            </div>
                                                        </div>

                                                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                                                            <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest mb-1">LOCAL TIP</p>
                                                            <p className="text-xs italic leading-relaxed">"{msg.data.route_insight.local_tip}"</p>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Budget Analysis Component */}
                                                {msg.data.budget_analysis && msg.data.budget_analysis.user_budget > 0 && (
                                                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl sm:rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-inner group overflow-hidden relative">
                                                        <div className="flex justify-between items-center mb-4">
                                                            <span className="text-gray-400 text-[10px] font-black uppercase tracking-widest">BUDGET MATCH</span>
                                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${msg.data.budget_analysis.gap > 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                                                                {msg.data.budget_analysis.gap > 0 ? `+${msg.data.budget_analysis.gap} SEK` : 'On Target'}
                                                            </span>
                                                        </div>
                                                        <div className="relative h-2 bg-gray-100 rounded-full mb-4">
                                                            <div
                                                                className="absolute h-full bg-amber-400 rounded-full transition-all duration-1000 ease-out"
                                                                style={{ width: `${Math.min(100, (msg.data.budget_analysis.user_budget / msg.data.budget_analysis.original_cost) * 100)}%` }}
                                                            ></div>
                                                        </div>
                                                        <div className="flex justify-between text-[11px] font-bold text-gray-500">
                                                            <span>Goal: {msg.data.budget_analysis.user_budget}</span>
                                                            <span>Est.: {msg.data.budget_analysis.original_cost}</span>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Suggestion Chips - Mobile: horizontal scroll, Desktop: wrap */}
                                                {msg.data.suggestion_chips && msg.data.suggestion_chips.length > 0 && (
                                                    <div className="w-full overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
                                                        <div className="flex gap-2 flex-nowrap sm:flex-wrap">
                                                            {msg.data.suggestion_chips.map((chip, idx) => (
                                                                <button
                                                                    key={idx}
                                                                    onClick={() => handleSendMessage(chip.label)}
                                                                    className="flex-shrink-0 bg-blue-50 hover:bg-amber-50 text-blue-700 hover:text-amber-700 border border-blue-200 hover:border-amber-200 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-[11px] sm:text-xs font-bold transition-all transform hover:-translate-y-0.5 shadow-sm active:scale-95 flex items-center gap-1.5 sm:gap-2"
                                                                >
                                                                    <span className="text-sm sm:text-base">{chip.icon || "✨"}</span>
                                                                    <span>{chip.label}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Visual Search Results */}
                                                {msg.data.interactive_cards && msg.data.interactive_cards.length > 0 && (
                                                    <div className="pt-2">
                                                        <TravelVisualSearch
                                                            destinations={msg.data.interactive_cards.map(card => ({
                                                                id: String(card.id),
                                                                name: card.title,
                                                                country: '',
                                                                imageUrl: card.image_url || 'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?q=80&w=1000&auto=format&fit=crop',
                                                                matchPercentage: parseInt(card.badge) || 95,
                                                                bestSeason: card.best_season || 'Nov - Mar',
                                                                aiReason: card.why_this_option,
                                                                price: String(card.total_price),
                                                                currency: 'SEK',
                                                                tags: [card.badge],
                                                                vibe: card.vibe,
                                                                duration: card.duration
                                                            }))}
                                                            onSelect={(dest) => { window.location.href = `/hotel/${dest.id}`; setIsOpen(false); }}
                                                            preferences="your dream trip"
                                                        />
                                                    </div>
                                                )}

                                                {/* Booking URL CTA from AI response */}
                                                {(msg.data as any)?.booking_url && !msg.searchResults && (
                                                    <a
                                                        href={(msg.data as any).booking_url}
                                                        className="mt-3 block w-full text-center bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-2.5 px-4 rounded-xl text-sm shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 active:scale-95"
                                                    >
                                                        {siteLanguage === 'sv' ? '✈️ Sök & Boka nu' : siteLanguage === 'tr' ? '✈️ Ara & Şimdi Rezerve Et' : '✈️ Search & Book Now'}
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}

                        {isLoading && (
                            <div className="flex justify-start animate-fadeIn">
                                <div className="bg-white p-4 sm:p-6 rounded-2xl rounded-tl-none shadow-xl border border-gray-50">
                                    <div className="flex flex-col space-y-2 sm:space-y-3">
                                        <span className="text-[9px] sm:text-[10px] text-gray-400 font-black uppercase tracking-widest animate-pulse">Lumi Assist thinking...</span>
                                        <div className="flex space-x-2">
                                            <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-blue-600 rounded-full animate-bounce transition-all duration-300"></div>
                                            <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-blue-600 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                            <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-blue-600 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area - Mobile optimized with safe area padding */}
                    <div className="p-3 sm:p-5 bg-white border-t border-gray-100 shadow-[0_-10px_20px_-5px_rgba(0,0,0,0.03)] safe-area-bottom">
                        <form
                            onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
                            className="flex gap-2 sm:gap-3"
                        >
                            {/* Microphone button */}
                            <button
                                type="button"
                                onClick={toggleListening}
                                disabled={isLoading}
                                className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl transition-all shadow-lg flex-shrink-0 touch-manipulation ${isListening
                                    ? 'bg-red-500 text-white animate-pulse shadow-red-500/30'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900'
                                    }`}
                                aria-label={siteLanguage === 'sv' ? 'Röstinmatning' : 'Voice input'}
                                title={siteLanguage === 'sv' ? (isListening ? 'Stoppa inspelning' : 'Tala för att skriva') : (isListening ? 'Stop recording' : 'Speak to type')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                </svg>
                            </button>

                            <div className="relative flex-grow group">
                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder={isListening ? (siteLanguage === 'sv' ? 'Lyssnar...' : 'Listening...') : getPlaceholder()}
                                    className={`w-full bg-gray-50 border border-gray-200 rounded-xl sm:rounded-2xl px-4 sm:px-5 py-3 sm:py-4 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition-all text-sm placeholder-gray-400 font-bold outline-none shadow-sm ${isListening ? 'border-red-300 bg-red-50' : ''}`}
                                    disabled={isLoading || isListening}
                                />
                                <div className="absolute inset-y-0 right-3 sm:right-4 flex items-center pointer-events-none opacity-0 group-focus-within:opacity-100 transition-opacity hidden sm:flex">
                                    <span className="text-[10px] font-black text-blue-600 active:scale-95">ENTER</span>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading || !inputValue.trim()}
                                className="bg-blue-600 hover:bg-amber-500 text-white hover:text-white p-3 sm:p-4 rounded-xl sm:rounded-2xl disabled:opacity-30 disabled:grayscale transition-all shadow-lg hover:shadow-amber-500/20 group transform hover:-translate-y-1 active:translate-y-0 flex-shrink-0"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 transform group-hover:rotate-12 transition-transform" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                                </svg>
                            </button>
                        </form>
                    </div>

                    <style>{`
                        @keyframes slideIn {
                            from { transform: translateX(100%); opacity: 0; }
                            to { transform: translateX(0); opacity: 1; }
                        }
                        @keyframes slideUp {
                            from { transform: translateY(100%); opacity: 0; }
                            to { transform: translateY(0); opacity: 1; }
                        }
                        @keyframes fadeInUp {
                            from { opacity: 0; transform: translateY(20px); }
                            to { opacity: 1; transform: translateY(0); }
                        }
                        /* Mobile: slide up, Desktop: slide in from right */
                        @media (max-width: 639px) {
                            .animate-slideIn { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                        }
                        @media (min-width: 640px) {
                            .animate-slideIn { animation: slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                        }
                        .animate-fadeInUp { animation: fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
                        /* Safe area support for iOS notch and home indicator */
                        .safe-area-top { padding-top: max(12px, env(safe-area-inset-top)); }
                        .safe-area-bottom { padding-bottom: max(12px, env(safe-area-inset-bottom)); }
                        /* Hide scrollbar but allow scrolling */
                        .no-scrollbar::-webkit-scrollbar { display: none; }
                        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                    `}</style>
                </div>
            )}
        </>
    );
};
